import { ComponentType, lazy } from 'react';
import { getEnv } from './env';
import { markAppUpdateAvailable, UNKNOWN_UPDATE_VERSION } from '../pwa/appUpdate';

// Track whether a version mismatch has been detected this session
let versionMismatchDetected = false;

/**
 * Check if the deployed version differs from the running version.
 * Returns true if there's a new deployment (chunk files have changed).
 */
const checkVersionMismatch = async (): Promise<boolean> => {
  if (versionMismatchDetected) return true;

  try {
    const response = await fetch('/version.json', {
      cache: 'no-cache',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) return false;

    // SPA fallback returns index.html (with 200) when version.json no longer exists.
    // Getting HTML back IS proof of a version mismatch — the old static file is gone.
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      console.info(
        `version.json returned non-JSON (${contentType}) — new deployment detected`
      );
      versionMismatchDetected = true;
      return true;
    }

    const data = await response.json();
    const deployedVersion = data.version;
    const currentVersion = getEnv('APP_VERSION');

    if (deployedVersion && currentVersion && deployedVersion !== currentVersion) {
      versionMismatchDetected = true;
      return true;
    }
  } catch {
    // If we can't reach version.json, assume network issue (not deployment)
  }
  return false;
};

const isChunkLoadError = (error: any): boolean => {
  return (
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch') ||
    error?.message?.includes('Load failed') ||
    error?.message?.includes('dynamically imported module') ||
    error?.name === 'ChunkLoadError'
  );
};

/**
 * Hand the deployment to the silent update policy. The thrown
 * VersionMismatchError below still reaches the ErrorBoundary, which reloads
 * once on its own — the old bundle is gone, so waiting for an idle moment
 * would only leave a broken screen up.
 */
const notifyNewVersion = () => {
  markAppUpdateAvailable(UNKNOWN_UPDATE_VERSION);
};

// Track permanently failed chunks to avoid infinite retries
const failedChunks = new Set<string>();

// Retry function with exponential backoff.
// On chunk errors, checks for version mismatch first to avoid
// wasting time retrying requests for chunks that no longer exist.
const retry = <T,>(
  fn: () => Promise<T>,
  retriesLeft = 3,
  delay = 1000,
  chunkName?: string
): Promise<T> => {
  return fn().catch(async (error) => {
    const isChunkError = isChunkLoadError(error);

    // If we've permanently failed this chunk, don't retry
    if (chunkName && failedChunks.has(chunkName)) {
      throw error;
    }

    // For chunk errors, check if a new version was deployed before wasting
    // time on retries that will never succeed
    if (isChunkError) {
      const isMismatch = await checkVersionMismatch();
      if (isMismatch) {
        console.info(
          `Chunk load failed for ${chunkName || 'unknown'} due to new deployment. ` +
          'Skipping retries — the error boundary reloads into the new build.'
        );
        if (chunkName) failedChunks.add(chunkName);
        notifyNewVersion();

        const versionError = new Error(
          `A new version of Homepoint has been deployed. Please refresh the page to continue.`
        );
        versionError.name = 'VersionMismatchError';
        throw versionError;
      }
    }

    // For non-deployment failures, retry with backoff
    if (retriesLeft === 0) {
      if (chunkName) failedChunks.add(chunkName);

      if (isChunkError) {
        const enhancedError = new Error(
          `Failed to load application module${chunkName ? ` (${chunkName})` : ''}. ` +
          `Please check your connection and try refreshing the page.`
        );
        enhancedError.name = 'ChunkLoadError';
        throw enhancedError;
      }

      throw error;
    }

    console.warn(
      `Module load failed, retrying in ${delay}ms. Retries left: ${retriesLeft}`,
      {
        error: error?.message || error,
        chunkName,
        isChunkError
      }
    );

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(retry(fn, retriesLeft - 1, Math.min(delay * 2, 5000), chunkName));
      }, delay);
    });
  });
};

/**
 * Wrapper around React.lazy that adds retry logic with exponential backoff
 * @param componentImport - Function that returns a dynamic import promise
 * @param componentName - Optional name for debugging and tracking (recommended)
 * @returns React lazy component with retry capability
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  componentName?: string
): React.LazyExoticComponent<T> {
  return lazy(() =>
    retry(componentImport, 3, 1000, componentName).then((module) => {
      if (componentName) {
        failedChunks.delete(componentName);
      }
      return module;
    })
  );
}

/**
 * Helper to preload a lazy component
 * Useful for preloading critical routes
 * @param componentImport - The import function
 * @param componentName - Optional name for debugging
 * @returns Promise that resolves when preloaded or rejects if failed
 */
export function preloadLazyComponent(
  componentImport: () => Promise<{ default: ComponentType<any> }>,
  componentName?: string
): Promise<void> {
  return componentImport()
    .then(() => {
      if (componentName) {
        console.log(`Preloaded: ${componentName}`);
      }
    })
    .catch((error) => {
      // Log preload failures but don't throw - component will load on demand
      console.warn(
        `Preload failed for ${componentName || 'component'}, will load on demand:`,
        error?.message
      );
    });
}

/**
 * Clear failed chunk tracking state.
 * Call after a page refresh to allow fresh loading attempts.
 */
export function clearAllRetryFlags(): void {
  failedChunks.clear();
  versionMismatchDetected = false;
}

/**
 * Preload multiple components in parallel
 * @param components - Array of [importFn, name] tuples
 */
export function preloadComponents(
  components: Array<[
    () => Promise<{ default: ComponentType<any> }>,
    string
  ]>
): Promise<void> {
  return Promise.all(
    components.map(([importFn, name]) =>
      preloadLazyComponent(importFn, name)
    )
  ).then(() => {
    console.log(`Preloaded ${components.length} components`);
  });
}

/**
 * Check if browser is idle and connection is fast enough for preloading
 */
export function canPreload(): boolean {
  // Check if browser supports connection API
  const connection = (navigator as any).connection ||
                     (navigator as any).mozConnection ||
                     (navigator as any).webkitConnection;

  if (connection) {
    // Don't preload on slow connections
    if (connection.effectiveType === 'slow-2g' ||
        connection.effectiveType === '2g') {
      return false;
    }

    // Don't preload if user has data saver enabled
    if (connection.saveData) {
      return false;
    }
  }

  return true;
}